import type { PillTone } from '../components/ui/Pill';
import type { Person } from './programs';
import type { StatsExtras } from './eventInfo';

export interface EventDay {
  date: string;
  label: string | null;
  startTime: string;
  endTime: string;
  dinner: boolean;
}

export interface ProfileFilters {
  regions: string[];
  careerStages: string[];
  disciplines: string[];
}

export type AudienceSourceKind = 'groups' | 'filters' | 'list' | 'public';

export interface AudienceSource {
  kind: AudienceSourceKind;
  core?: boolean;
  groupIds?: string[];
  filters?: ProfileFilters;
}

export interface Audience {
  key: string;
  name: string;
  source: AudienceSource;
  days: string[] | null;
  funded: boolean;
  opensOn: string | null;
  sentAt: string | null;
  sentCount: number;
  invitedOutside?: { on: string; via: string } | null;
  people?: number;
  coming?: number;
}

export const SOURCE_LABEL: Record<AudienceSourceKind, string> = {
  groups: 'From member groups',
  filters: 'Members by region, career stage or discipline',
  list: 'Added by hand',
  public: 'Public registrations (website form, Eventbrite, walk-ins)',
};

export const audienceName = (audiences: Audience[], key: string | null | undefined) =>
  audiences.find((a) => a.key === key)?.name ?? '—';

export interface EventSettings {
  hotelMinTravelHours: number;
  extraNightMinFlightHours: number;
  hotelNights: number;
  capacity: number | null;
  hotelBlockRooms?: number | null;
  hotelBlockFrom?: string | null;
  hotelBlockTo?: string | null;
  checkinProvider?: string | null;
  keyContacts?: { label: string | null; name: string | null; email: string | null; phone: string | null }[];
}

export interface EventView {
  id: string;
  title: string;
  titleFr: string | null;
  kind: string;
  mode: string;
  city: string | null;
  venue: string | null;
  startsAt: string;
  endsAt: string;
  days: EventDay[];
  settings: EventSettings;
  audiences: Audience[];
  formLink: string | null;
  evaluationUrl: string | null;
  contactEmail: string | null;
  zoomWebinarId: string | null;
  documents: Record<string, { at: string; count: number }>;
  closedAt: string | null;
  locked: boolean;
  lockedFrom: string | null;
  unlockedUntil: string | null;
  statsGeneratedAt: string | null;
  counts: EventCounts | null;
  composition: EventComposition | null;
  outreach: OutreachDetails | null;
  notes: string | null;
  parentEventId: string | null;
}

export interface EventComposition {
  source: string | null;
  shares: { group: 'role' | 'identity'; label: string; percent: number }[];
}

export interface EventCounts {
  trainees: number | null;
  faculty: number | null;
  other: number | null;
  registered: number | null;
  total: number | null;
}

export interface OutreachDetails {
  institution?: string | null;
  topic?: string | null;
  audience?: string | null;
  format?: 'in_person' | 'online' | 'hybrid';
  datePrecision?: 'month' | null;
}

export interface EventBrief {
  id: string;
  title: string;
  kind: string;
  days: EventDay[];
  startsAt: string;
}

export interface EventListItem extends EventView {
  invitees: number;
  byStatus: Record<string, number>;
  checkedIn: number;
  attended?: number;
  attendedFromCounts?: boolean;
  subEvents?: number;
  subEventList?: { id: string; title: string; days: EventDay[] }[];
}

export interface LiveDay {
  date: string;
  label: string | null;
  checkedIn: number;
  expected: number;
  byAudience: { key: string; name: string; checkedIn: number; expected: number }[];
}

export interface CheckinLink {
  id: string;
  label: string | null;
  validFrom: string;
  expiresAt: string;
  revokedAt: string | null;
  state: 'valid' | 'not_yet' | 'expired' | 'revoked';
  checkins: number;
  lastUsedAt: string | null;
  url?: string;
}

export interface EventDetail {
  event: EventView;
  parent?: EventBrief | null;
  subEvents?: EventBrief[];
  byStatus: Record<string, number>;
  byAudience: Record<string, number>;
  live: LiveDay[];
  links: CheckinLink[];
  statuses: string[];
  travelModes: string[];
}

export interface Flag {
  rule: string;
  severity: 'fail' | 'unknown';
  message: string;
}

export interface Needs {
  attendanceMode?: 'in_person' | 'virtual';
  attendDays?: string[];
  hotel?: boolean;
  nights?: number;
  travelMode?: string;
  travelHours?: number;
  flightHours?: number;
  parking?: boolean;
  dietary?: string;
  accessibility?: string;
  emhic?: boolean;
  fundingRequested?: boolean;
  travellingFrom?: string;
  bankingOnFile?: 'yes' | 'no' | 'unsure';
}

export interface Invitee {
  id: string;
  audienceKey: string | null;
  role: string | null;
  status: string;
  source: string;
  invitedAt: string | null;
  registeredAt: string | null;
  confirmedAt: string | null;
  confirmationSentAt: string | null;
  person: Person;
  affiliation: string | null;
  checkedIn: string[];
  needs?: Needs;
  eligibility?: {
    hotelEligible: boolean | null;
    nights: number;
    extraNight: boolean;
    funded: boolean;
    flags: Flag[];
  } | null;
  checkinCode?: string;
  note?: string | null;
  history?: StatusChange[];
}

export interface StatusChange {
  from: string | null;
  to: string;
  at: string;
  by: string | null;
  byEmail?: string | null;
  source: string;
  note: string | null;
}

export interface CountRow {
  value: string;
  count: number;
}

export interface EventStats {
  stats: {
    attendees: number;
    virtual: number;
    byDay: { date: string; label: string | null; count: number }[];
    byAudience: CountRow[];
    byRole: CountRow[];
    byRegion: CountRow[];
    byCareerStage: CountRow[];
    byDiscipline: CountRow[];
    equity: {
      answeredBy: number;
      booleans: { key: string; label: string; yes: number; answered: number }[];
      genderIdentity: { items: CountRow[]; total: number };
    };
  };
  ratings: {
    responses: number;
    numeric: { key: string; label: string; answered: number; mean: number | null }[];
    eventRelevance: { answered: number; items: CountRow[] };
  };
  extras?: StatsExtras;
  document?: { html: string };
}

export const STATUS_TONE: Record<string, PillTone> = {
  invited: 'planned',
  registered: 'running',
  confirmed: 'active',
  declined: 'inactive',
  waitlisted: 'planned',
  attended: 'healthy',
  no_show: 'failed',
};

export const KIND_LABEL: Record<string, string> = {
  national: 'National event',
  regional: 'Regional meeting',
  outreach: 'Outreach talk / visit',
  pi_meeting: 'PI meeting',
};
export const FORMAT_LABEL: Record<string, string> = { in_person: 'In person', online: 'Online', hybrid: 'Hybrid' };
export const COUNT_LABEL: [keyof EventCounts, string][] = [
  ['trainees', 'Trainees'],
  ['faculty', 'Faculty and academics'],
  ['other', 'Other'],
  ['total', 'Total attended'],
  ['registered', 'Registered or RSVP'],
];

export const monthLabel = (date: string, locale = 'en-CA') =>
  new Date(`${date}T12:00:00Z`).toLocaleDateString(locale, { timeZone: 'UTC', month: 'long', year: 'numeric' });

export const eventDates = (e: Pick<EventView, 'days' | 'outreach'>) => {
  if (!e.days.length) return '';
  if (e.outreach?.datePrecision === 'month') return monthLabel(e.days[0].date);
  return e.days.length === 1
    ? dayLabel(e.days[0].date)
    : `${dayLabel(e.days[0].date)} – ${dayLabel(e.days[e.days.length - 1].date)}`;
};
export const TRAVEL_LABEL: Record<string, string> = {
  flight: 'Flight',
  train: 'Train',
  car: 'Car',
  bus: 'Bus',
  transit: 'Transit',
  local: 'Local',
  other: 'Other',
};
export const DOCUMENTS: [string, string, string][] = [
  ['hotel', 'Hotel list', 'Who needs a room, nights, accessibility'],
  ['catering', 'Catering numbers', 'Numbers per day with dietary counts (no names)'],
  ['dinner', 'Dinner lists', 'Guests and dietary needs per dinner'],
  ['nametags', 'Nametags', 'Name, pronouns, role, affiliation, QR of the check-in code'],
  ['checkin', 'Check-in sheets', 'Paper fallback per day'],
];

export const dayLabel = (date: string, locale = 'en-CA') =>
  new Date(`${date}T12:00:00Z`).toLocaleDateString(locale, {
    timeZone: 'UTC',
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

/** How a person reached the invitee list. */
export const cameFrom = (i: { source: string; status: string }): string => {
  if (i.source === 'eventbrite') return 'Eventbrite';
  if (i.source === 'registration') return 'Confirmation form';
  if (i.status === 'invited') return 'No response yet';
  return 'Added by staff';
};

export const torontoToday = () =>
  new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Toronto',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());

export const splitList = (v: string) =>
  v
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
